import { Abi, AccountInvocationItem, Call, Contract, transaction } from 'starknet'
import { Network } from './token.service'
import poolNftAbi from '../contractsAbi/pool_nft.json'
import { accountBraavosMultisig, accountCairoVersion, getProvider, numberToCallParam } from './amm'
import BigNumber from 'bignumber.js'
import { getToken, toUint256 } from '../model/tokens/helper'
import { Transaction } from '../atoms'
import { getPrincipalAndFeesOfPosition } from './position'
import { CLPosition } from '../model/clpool'
import { getAllCLPoolForFee, getFactoryAddress } from '../model/clpool/helper'
import { FAIL_ALL_TXS, FAIL_CONTRACT, getCurrentTick, getStarknet } from './amm_v3'
import { BulkCall, bulkCallContract } from '../blockchainUtils/bulkCall'
import { MAX_TICK } from '../components/PoolsPage/helper'

export function getNFTFactoryAddress(network: Network): string {
    if (network === 'goerli-alpha') return '0x52d99ab5835a0c295567dcdd40e9e281378d26d02ecca7b31e33dea99bc02ea' // '0x53005f7db146bdebf07bbfa28aafac6ce01b9e85f783d7b4a939a0c0f83dc0a' //'0x0174d1b49fc26f3a905d7e4755c5b7d4e885db112fa7bb417868178632e1a72d'
    if (network === 'sepolia-alpha') return '0x2162429d1d1b6db3571a052244a91aa8d7198fe972641e6731bedc66d660cad' // '0x53005f7db146bdebf07bbfa28aafac6ce01b9e85f783d7b4a939a0c0f83dc0a' //'0x0174d1b49fc26f3a905d7e4755c5b7d4e885db112fa7bb417868178632e1a72d'

    return '0xfff107e2403123c7df78d91728a7ee5cfd557aec0fa2d2bdc5891c286bbfff'
}

export function getLensAddress(network: Network): string {
    if (network === 'goerli-alpha') return '0xb6a33c4bc5db030f0b10cb3af2328e6b3116b16e2dd61487106261926005ad'
    if (network === 'sepolia-alpha') return '0x2e7d8d9b053e3e9981c05a259427db5f965a75f75f5bdb20fc29697e954ede7'

    return '0x32a08e81590831a01c8c1d27ee8a162adce2dac31c9c4cda87d5e8661ec28f1'
}

export async function getNFTFactoryContract(network: Network) {
    return new Contract(poolNftAbi as Abi, getNFTFactoryAddress(network), await getProvider(network))
}

export async function getNFTBalanceOfAddress(network: Network, wallet: string) {
    try {
        const response = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'balance_of',
            contractAddress: getNFTFactoryAddress(network), // change to pool contract
            calldata: [wallet]
        })
        return Number(response.result[0])
    } catch (e: any) {
        console.log('getNFTBalanceOfAddress error ', e.message)
        return 0
    }
}

export async function getTokenOfOwnerByIndex(network: Network, wallet: string, indexOfToken: number) {
    try {
        const response = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'tokenOfOwnerByIndex',
            contractAddress: getNFTFactoryAddress(network), // change to pool contract
            calldata: [numberToCallParam(wallet), numberToCallParam(indexOfToken), numberToCallParam(0)] // TODO -> how do i use 2 args for index?
        })
        return Number(response.result[0])
    } catch (e: any) {
        console.log('getTokenOfOwnerByIndex error ', e.message)
        return 0
    }
}

let maxRetries = 5
export async function getTokensOfOwner(network: Network, wallet: string, selectedPosition?: number): Promise<CLPosition[]> {
    try {
        // patch for buggy enumerable
        if (wallet === '0x04e3d8b199f5a817e28d6facc7cbe5b5d49643df8e5bbfc207a10a4a4dd08e23' || wallet === '0x4e3d8b199f5a817e28d6facc7cbe5b5d49643df8e5bbfc207a10a4a4dd08e23') {
            const indexes = [9652, 187765, 188082]
            const nonce = await (await getProvider(network)).getNonceForAddress(wallet)
            let pools = await bulkCallGetNFTPositionsLegacy(network, indexes, wallet, nonce)

            let res = await Promise.all(pools)
            res = res.filter(function (element) {
                return element !== undefined
            })
            if (selectedPosition !== undefined && selectedPosition !== -1) {
                const selectedPos = res.find((item) => item?.positionIndex === selectedPosition)
                return [selectedPos] as CLPosition[]
            }

            console.log('YSYS address of buggy nft, res=', res)
            return res as CLPosition[]
        }

        let positions = await getPositionsFromLens(network, wallet)
        console.log('YSYSYS positions=', positions)
        let position: Partial<CLPosition> = undefined as unknown as Partial<CLPosition>
        if (selectedPosition !== undefined && selectedPosition !== -1)
            position = positions.positions.find((position) => position && position.positionIndex === selectedPosition) as Partial<CLPosition>

        const nonce = await (await getProvider(network)).getNonceForAddress(wallet)
        let pools = await bulkCallGetNFTPositions(network, position ? [position!] : positions.positions, wallet, nonce)

        pools = pools.filter(function (pools) {
            return pools !== undefined
        })

        if (pools[0] !== undefined) return pools as CLPosition[]
        else if (maxRetries > 0) {
            maxRetries = maxRetries - 1
            return getTokensOfOwner(network, wallet, selectedPosition)
        } else {
            maxRetries = 5
            return []
        }
    } catch (e: any) {
        console.log('getTokensOfOwner error ', e.message)
        return []
    }
}

export async function mint(
    network: string,
    address: string,
    token0: string,
    token1: string,
    fee: number,
    tickLower: number,
    tickUpper: number,
    amount0Desired: string,
    amount1Desired: string,
    amount0Min: string,
    amount1Min: string,
    nftAddress: string,
    sqrtPriceIfNewPool?: string
) {
    const starknet = await getStarknet()
    const approves: Array<Call> = []
    if (sqrtPriceIfNewPool !== undefined) {
        if (BigNumber(token0).gte(BigNumber(token1))) {
            throw 'Token order is incorrect'
        }
        approves.push({
            contractAddress: getFactoryAddress(network as Network),
            entrypoint: 'create_and_initialize_pool',
            calldata: [token0, token1, fee, ...toUint256(sqrtPriceIfNewPool)]
        })
    }
    if (Number(amount0Desired) > 0) {
        approves.push({
            contractAddress: token0,
            entrypoint: 'approve',
            calldata: [numberToCallParam(nftAddress), ...toUint256(amount0Desired)]
        })
    }
    if (Number(amount1Desired) > 0) {
        approves.push({
            contractAddress: token1,
            entrypoint: 'approve',
            calldata: [numberToCallParam(nftAddress), ...toUint256(amount1Desired)]
        })
    }
    const calls: Array<Call> = [
        ...approves,
        {
            entrypoint: 'mint',
            contractAddress: nftAddress,
            calldata: [
                address,
                token0,
                token1,
                numberToCallParam(fee),
                numberToCallParam(tickLower),
                numberToCallParam(tickUpper),
                ...toUint256(amount0Desired),
                ...toUint256(amount1Desired),
                ...toUint256(amount0Min),
                ...toUint256(amount1Min)
            ]
        }
    ]

    if (FAIL_ALL_TXS) {
        calls.push({
            contractAddress: FAIL_CONTRACT,
            entrypoint: 'fail',
            calldata: []
        })
    }

    try {
        // @ts-ignore
        let tx = await starknet.account.execute(calls)

        const transaction: Transaction = {
            hash: tx.transaction_hash,
            network,

            // pool_id: poolId,
            status: 'new',
            token1: token0,
            token1Amount: amount0Desired,
            token2: token1,
            token2Amount: amount1Desired,
            // token2: getPoolOtherToken(network, poolId, tokenFrom),
            // token2Amount: minAmountOut,
            type: 'mint',
            dismiss: false
        }

        return { tx: transaction, success: true }
    } catch (e) {
        // throw new Error('Failed to create mint tx')
        return { error: 'Failed to mint', success: false }
    }
}

export async function simulateMintTx(
    network: string,
    address: string,
    token0: string,
    token1: string,
    fee: number,
    tickLower: number,
    tickUpper: number,
    amount0Desired: string,
    amount1Desired: string,
    amount0Min: string,
    amount1Min: string,
    nftAddress: string,
    sqrtPriceIfNewPool?: string
) {
    const starknet = await getStarknet()
    const approves: Array<Call> = []
    if (sqrtPriceIfNewPool !== undefined) {
        if (BigNumber(token0).gte(BigNumber(token1))) {
            throw 'Token order is incorrect'
        }
        approves.push({
            contractAddress: getFactoryAddress(network as Network),
            entrypoint: 'create_and_initialize_pool',
            calldata: [token0, token1, fee, ...toUint256(sqrtPriceIfNewPool)]
        })
    }
    if (Number(amount0Desired) > 0) {
        approves.push({
            contractAddress: token0,
            entrypoint: 'approve',
            calldata: [numberToCallParam(nftAddress), ...toUint256(amount0Desired)]
        })
    }
    if (Number(amount1Desired) > 0) {
        approves.push({
            contractAddress: token1,
            entrypoint: 'approve',
            calldata: [numberToCallParam(nftAddress), ...toUint256(amount1Desired)]
        })
    }
    const calls: Array<Call> = [
        ...approves,
        {
            entrypoint: 'mint',
            contractAddress: nftAddress,
            calldata: [
                address,
                token0,
                token1,
                numberToCallParam(fee),
                numberToCallParam(tickLower),
                numberToCallParam(tickUpper),
                ...toUint256(amount0Desired),
                ...toUint256(amount1Desired),
                ...toUint256(amount0Min),
                ...toUint256(amount1Min)
            ]
        }
    ]

    const handler = accountCairoVersion() === '1' ? transaction.fromCallsToExecuteCalldata_cairo1 : transaction.fromCallsToExecuteCalldata
    const hcalls = handler(calls)
    const nonce = await (await getProvider(network as Network)).getNonceForAddress(address)
    console.log('YSYSYS nonce=', nonce, 'accountCairoVersion=', accountCairoVersion())
    let invocation: AccountInvocationItem = {
        signature: [],
        // @ts-ignore
        type: 'INVOKE_FUNCTION',
        nonce: nonce,
        entrypoint: '__execute__',
        contractAddress: address,
        calldata: hcalls,
        version: '340282366920938463463374607431768211457'
    }
    const isBraavosMultisig = accountBraavosMultisig()
    if (isBraavosMultisig) {
        const wrapped_calls = handler([
            {
                contractAddress: address,
                entrypoint: 'sign_pending_multisig_transaction',
                calldata: [hcalls.length, ...hcalls, 0, 0, 1]
            }
        ])
        invocation = {
            signature: ['0', '0'],
            // @ts-ignore
            type: 'INVOKE_FUNCTION',
            nonce: nonce,
            entrypoint: '__execute__',
            contractAddress: address,
            calldata: wrapped_calls,
            version: '340282366920938463463374607431768211457'
        }
    }
    try {
        const response = await (
            await getProvider(network as Network)
        ).getSimulateTransaction([invocation], {
            blockIdentifier: 'pending',
            skipValidate: true
        })
        console.log('YSYS mint tx response=', response)
        // @ts-ignore
        const res = response[0].transaction_trace.execute_invocation.result
        return true
    } catch (e) {
        console.log('YSYS mint tx err=', e)
        return false
    }
}

export async function bulkCallGetTokenOfOwnerByIndex(network: Network, wallet: string, indexesOfTokens: number[]): Promise<number[]> {
    const calls: BulkCall[] = []
    indexesOfTokens.forEach((indexOfToken) => {
        calls.push({
            call: {
                entrypoint: 'tokenOfOwnerByIndex',
                contractAddress: getNFTFactoryAddress(network), // change to pool contract
                calldata: [numberToCallParam(wallet), numberToCallParam(indexOfToken), numberToCallParam(0)] // TODO -> how do i use 2 args for index?
            },
            responseParser: {
                kind: 'fixed',
                length: 2
            }
        })
    })
    const indexes = (await bulkCallContract(network, calls)).map((res) => {
        return Number(res.result[0])
    })
    return indexes
}

export async function bulkCallGetNFTPositions(network: Network, positions: Partial<CLPosition>[], walletAddress: string, nonce: string) {
    try {
        const positionsWithValues = await Promise.all(
            positions.map(async (position) => {
                try {
                    const response = await getPrincipalAndFeesOfPosition(network, position as any, walletAddress, nonce)
                    position.value = response?.total
                    position.fees = response?.fees

                    return position
                } catch (error) {
                    console.error(`Error fetching position data: ${error}`)
                    return null
                }
            })
        )
        return positionsWithValues.filter((position) => position !== null)
    } catch (e: any) {
        console.log('errrorrr ', e.message)
        return []
    }
}

export async function bulkCallGetNFTPositionsLegacy(network: Network, positionIndexes: number[], walletAddress: string, nonce: string) {
    const calls: BulkCall[] = []

    positionIndexes.forEach((positionIndex) => {
        calls.push({
            call: {
                entrypoint: 'positions',
                contractAddress: getNFTFactoryAddress(network), // change to pool contract
                calldata: [numberToCallParam(positionIndex), numberToCallParam(0)]
            },
            responseParser: {
                kind: 'fixed',
                length: 12
            }
        })
    })
    const positionResponses = (await bulkCallContract(network, calls)).map(async (positionResponse, index) => {
        try {
            const poolNft = {
                // add interface
                positionIndex: positionIndexes[index], // change this name to tokenId
                token0: positionResponse.result[0],
                token1: positionResponse.result[1],
                fee: Number(positionResponse.result[2]),
                tickLower: Number(positionResponse.result[3]),
                tickUpper: Number(positionResponse.result[4]),
                liquidity: BigNumber(positionResponse.result[5]).toString(),
                feeGrowthInside0LastX128: BigNumber(positionResponse.result[6]).toString(),
                feeGrowthInside1LastX128: BigNumber(positionResponse.result[7]).toString(),
                tokensOwed0: BigNumber(positionResponse.result[8]).toString(),
                tokensOwed1: BigNumber(positionResponse.result[9]).toString(),
                value: {} as any,
                fees: {} as any,
                poolAddress: '',
                poolCurrentTick: 0
            }
            const response = await getPrincipalAndFeesOfPosition(network, poolNft as any, walletAddress, nonce)
            poolNft.value = response?.total
            poolNft.fees = response?.fees

            const poolForPosition = await getAllCLPoolForFee(network, getToken(network!, poolNft.token0)!, getToken(network, poolNft.token1)!, poolNft.fee)
            poolNft.poolAddress = poolForPosition.address

            const currentTick = await getCurrentTick(network, poolForPosition.address)
            console.log('currentTick ', currentTick)
            if (currentTick || currentTick === 0) poolNft.poolCurrentTick = (currentTick || 0) + MAX_TICK
            return poolNft
        } catch (err: any) {
            console.log('errorrr ', err.message)
            return undefined
        }
    })

    return positionResponses
}

export async function getPositionsFromLens(network: Network, wallet: string): Promise<{ positions: Partial<CLPosition>[]; out_of_range_cnt: number }> {
    try {
        const response = await (
            await getProvider(network)
        ).callContract({
            entrypoint: 'get_account_positions',
            contractAddress: getLensAddress(network), // change to pool contract
            calldata: [numberToCallParam(wallet)]
        })
        const poolsData = response.result.slice(1, response.result.length)
        const positionsData = breakArrayToObjects(poolsData)
        let idx = 1
        let out_of_range_cnt = 0
        while (idx < response.result.length) {
            const oor = Number(response.result[idx + 15]) === 0
            if (oor) out_of_range_cnt += 1
            idx += 19
        }
        return { positions: positionsData, out_of_range_cnt: out_of_range_cnt }
    } catch (e: any) {
        console.log('getPositionsFromLens error ', e.message)
    }
    return { positions: [], out_of_range_cnt: 0 }
}

function breakArrayToObjects(arr: string[]): Partial<CLPosition>[] {
    const result: Partial<CLPosition>[] = []

    for (let i = 0; i < arr.length; i += 19) {
        const obj = {
            poolAddress: arr[i],
            positionIndex: Number(arr[i + 1]),
            token0: arr[i + 3],
            token1: arr[i + 4],
            fee: Number(arr[i + 5]),
            tickLower: Number(arr[i + 6]),
            tickUpper: Number(arr[i + 7]),
            liquidity: new BigNumber(arr[i + 8]).toString(),
            feeGrowthInside0LastX128: new BigNumber(arr[i + 9]).toString(),
            feeGrowthInside1LastX128: new BigNumber(arr[i + 11]).toString(),
            tokensOwed0: new BigNumber(arr[i + 13]).toString(),
            tokensOwed1: new BigNumber(arr[i + 14]).toString(),
            poolCurrentTick: Number(arr[i + 16]),
            sqrtPrice: Number(arr[i + 17])
        } as Partial<CLPosition>

        result.push(obj)
    }

    return result
}
